import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { AppConfigResponse } from '../../../models/prompt-library.models';
import { PromptLibraryItem } from '../../../../shared/models/prompt.models';

type RegRadarRow = { english?: string; japanese?: string; chinese?: string };

/**
 * Maps API row fields to the active UI language (matches LanguageChangeService / prompt-library).
 */
export function pickRegRadarQuestionText(row: RegRadarRow, lang: string): string {
  const en = (row?.english ?? '').trim();
  const jp = (row?.japanese ?? '').trim();
  const cn = (row?.chinese ?? '').trim();
  if (lang === 'jp') {
    return jp || en;
  }
  if (lang === 'zhCN') {
    return cn || en;
  }
  return en || jp || cn;
}

/**
 * Turns the curated RegRadar slice into prompt cards (same heading keys as agent_config.json).
 */
export function mapRegRadarSliceToPromptLibrary(slice: AppConfigResponse, lang: string): PromptLibraryItem[] {
  const reg = slice?.reg_radar_data ?? [];
  const app = slice?.app_config_data ?? [];
  const items: PromptLibraryItem[] = [];

  let regIdx = 0;
  for (const row of reg) {
    const text = pickRegRadarQuestionText(row, lang);
    if (!text) {
      continue;
    }
    items.push({
      id: `hub_reg_${regIdx++}`,
      title: 'REG_RADAR_TITLE',
      prompt: text,
      literalContent: true
    });
  }

  let appIdx = 0;
  for (const row of app) {
    const text = pickRegRadarQuestionText(row, lang);
    if (!text) {
      continue;
    }
    items.push({
      id: `hub_app_${appIdx++}`,
      title: 'EXPERT_CURATED_QUESTIONS_TITLE',
      prompt: text,
      literalContent: true
    });
  }

  return items;
}

/**
 * Holds the regulations hub card RegRadar slice so {@link PromptSectionComponent} can show
 * API questions instead of static agent_config.json entries until the user returns to the hub.
 */
@Injectable({
  providedIn: 'root'
})
export class RegulationsHubPromptLibraryStore {
  private readonly hubSlice = new BehaviorSubject<AppConfigResponse | null>(null);

  readonly hubSlice$: Observable<AppConfigResponse | null> = this.hubSlice.asObservable();

  setHubRegRadarSlice(slice: AppConfigResponse | null): void {
    this.hubSlice.next(slice);
  }

  clearHubRegRadarSlice(): void {
    this.hubSlice.next(null);
  }

  getHubSlice(): AppConfigResponse | null {
    return this.hubSlice.value;
  }
}
